import { displayUnitRange, formatUnitValue, unitLabel } from '../../units/unitConversions';
const DIAMETER_COMPLETE = /^\d+(?:[.,]\d+)?$/;
const DIAMETER_INCOMPLETE = /^\d*[.,]?$/;
const DESIGNATION_COMPLETE = /^([A-Za-z]+)(\d+)$/;
const DESIGNATION_INCOMPLETE = /^[A-Za-z]*\d*$/;
const HOLE_QUERY_PATTERN = /^[A-Z]+\d+$/;
const SHAFT_QUERY_PATTERN = /^[a-z]+\d+$/;

export const normalizeFitDesignationCase = (value, type) =>
  value.replace(/[A-Za-z]+/, (letters) =>
    type === 'hole' ? letters.toUpperCase() : letters.toLowerCase()
  );

export const getInitialFitInputs = (searchParams) => {
  const hasFitQuery = ['diameter', 'hole', 'shaft'].some((key) => searchParams.has(key));
  if (!hasFitQuery) return { diameter: '25', hole: 'H7', shaft: 'g6' };

  const diameter = (searchParams.get('diameter') ?? '').trim();
  const numericDiameter = Number(diameter.replace(',', '.'));
  const validDiameter = DIAMETER_COMPLETE.test(diameter)
    && Number.isFinite(numericDiameter)
    && numericDiameter >= 1
    && numericDiameter <= 500;
  const hole = (searchParams.get('hole') ?? '').trim();
  const shaft = (searchParams.get('shaft') ?? '').trim();

  return {
    diameter: validDiameter ? diameter : '',
    hole: HOLE_QUERY_PATTERN.test(hole) ? hole : '',
    shaft: SHAFT_QUERY_PATTERN.test(shaft) ? shaft : '',
  };
};

export const parseFitInputs = ({ diameter, hole, shaft }, { rangeUnitSystem = 'metric' } = {}) => {
  const diameterText = (diameter ?? '').trim();
  const holeText = (hole ?? '').trim();
  const shaftText = (shaft ?? '').trim();

  if (!diameterText || !holeText || !shaftText) return { status: 'incomplete' };

  if (!DIAMETER_COMPLETE.test(diameterText)) {
    return DIAMETER_INCOMPLETE.test(diameterText)
      ? { status: 'incomplete' }
      : { status: 'invalid', message: 'Enter a valid nominal diameter.' };
  }

  const numericDiameter = Number(diameterText.replace(',', '.'));
  if (!Number.isFinite(numericDiameter) || numericDiameter < 1 || numericDiameter > 500) {
    return {
      status: 'invalid',
      message: `Nominal diameter must be between ${displayUnitRange(1, 500, 'mm', rangeUnitSystem, { separator: 'and' })}.`,
    };
  }

  const holeMatch = holeText.match(DESIGNATION_COMPLETE);
  const shaftMatch = shaftText.match(DESIGNATION_COMPLETE);
  if (!holeMatch || !shaftMatch) {
    if (DESIGNATION_INCOMPLETE.test(holeText) && DESIGNATION_INCOMPLETE.test(shaftText)) {
      return { status: 'incomplete' };
    }
    return { status: 'invalid', message: 'Use designations such as H7 and g6.' };
  }

  if (holeMatch[1] !== holeMatch[1].toUpperCase()) {
    return { status: 'invalid', message: 'Use uppercase letters for the hole designation.' };
  }
  if (shaftMatch[1] !== shaftMatch[1].toLowerCase()) {
    return { status: 'invalid', message: 'Use lowercase letters for the shaft designation.' };
  }

  return {
    status: 'valid',
    diameter: numericDiameter,
    hole: holeText,
    shaft: shaftText,
  };
};

export const getFitPrecision = (result) => {
  const values = [
    result.hole.limits.minimumMm,
    result.hole.limits.maximumMm,
    result.shaft.limits.minimumMm,
    result.shaft.limits.maximumMm,
  ];
  return values.some((value) =>
    Math.abs(Number(value) * 1000 - Math.round(Number(value) * 1000)) > 1e-8
  ) ? 4 : 3;
};

export const formatMillimetres = (value, precision, unitSystem = 'metric') =>
  unitSystem === 'inch' ? formatUnitValue(value, 'mm', unitSystem) : Number(value).toFixed(precision);

export const formatMicrometres = (value, signed = false, unitSystem = 'metric') => {
  if (unitSystem === 'inch') return `${formatUnitValue(value, 'um', unitSystem, { signed })} ${unitLabel('um', unitSystem)}`;
  const normalized = Object.is(Number(value), -0) ? 0 : Number(value);
  const prefix = signed && normalized > 0 ? '+' : '';
  return `${prefix}${normalized} µm`;
};
